'use client'
// LOGIN task (README_CURRENT_TASKS §4) + VISITOR FACADE (§6): the desk's
// sign-in entry.
//
// SKELETON component: structure + md-* contract classes only; every visual
// class arrives through the AuthEntrySkin the owning surface passes (no-CSS
// rule, docs/25). Identity comes from the SERVER-derived boot grant
// (selectAccessGrant().viewer — the NextAuth JWT resolved by the desk host),
// so SSR and hydration agree without any next-auth client code on first load.
//
// Entry/leave go through the visitor boundary. The entry dialog is mounted
// ONCE by VisitorProvider (lazy chunk on first openEntry); after enter or
// leave the provider's desk refresh policy RELOADS the page: capabilities,
// the boot payload, pin-chain unlocks, the legacy model, and the PHP-session
// identity bridge are all boot-derived, so a full re-boot is the only honest
// refresh.

import { useState } from 'react'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectAccessGrant, selectMainState } from '@/msgdeskRuntime/state'
import { useVisitor } from '@/app/visitor/useVisitor'

export interface AuthEntrySkin {
  root?: string
  signInButton?: string
  identity?: string
  identityLink?: string
  signOutButton?: string
  membershipButton?: string
  membershipStatus?: string
  membershipError?: string
}

export function AuthEntry({ skin = {} }: { skin?: AuthEntrySkin }) {
  const { state } = useSubsStore()
  const visitor = useVisitor()
  const grant = selectAccessGrant(state)
  const main = selectMainState(state)
  const viewer = grant.viewer
  const [membershipBusy, setMembershipBusy] = useState(false)
  const [membershipError, setMembershipError] = useState('')

  const signOut = () => {
    void visitor.leave({ redirect: false })
  }

  const requestMembership = async () => {
    if (membershipBusy) return
    setMembershipBusy(true)
    setMembershipError('')
    const membershipRootCodeKey = grant.members_root?.code_key ?? String(main?.codeKey ?? '')
    try {
      const response = await fetch(
        `/api/msgdesk/model?action=join_members&this_mode=msg&nextjs=1&grants=1&codekey=${encodeURIComponent(membershipRootCodeKey)}`,
        { method: 'GET', credentials: 'same-origin' },
      )
      const body = (await response.text()).trim()
      if (body === 'pending' || body === 'member') {
        window.location.reload()
        return
      }
      setMembershipError('Request failed')
    } catch {
      setMembershipError('Request failed')
    }
    setMembershipBusy(false)
  }

  const membership = viewer.members === 'off' || viewer.members === 'member'
    ? null
    : viewer.members === 'pending'
      ? (
        <span className={`md-auth-entry__membership-status ${skin.membershipStatus ?? skin.identity ?? ''}`}>
          Membership pending
        </span>
      )
      : viewer.is_logged
        ? (
          <button
            type="button"
            className={`md-auth-entry__membership-request ${skin.membershipButton ?? skin.signInButton ?? ''}`}
            disabled={membershipBusy}
            onClick={requestMembership}
          >
            {membershipBusy ? 'Requesting…' : 'Request access'}
          </button>
        )
        : null

  return (
    <div className={`md-auth-entry ${skin.root ?? ''}`}>
      {membership}
      {membershipError ? (
        <span className={`md-auth-entry__membership-error ${skin.membershipError ?? skin.membershipStatus ?? ''}`}>
          {membershipError}
        </span>
      ) : null}
      {viewer.is_logged ? (
        <span className={`md-auth-entry__identity ${skin.identity ?? ''}`}>
          <a
            className={`md-auth-entry__identity-link ${skin.identityLink ?? ''}`}
            href={`/${viewer.code_key}`}
            title={viewer.is_owner ? 'Signed in as this message' : 'Your message'}
          >
            @{viewer.name_key || viewer.code_key}
          </a>
          <button
            type="button"
            className={`md-auth-entry__sign-out ${skin.signOutButton ?? ''}`}
            onClick={signOut}
          >
            Sign out
          </button>
        </span>
      ) : (
        <button
          type="button"
          className={`md-auth-entry__sign-in ${skin.signInButton ?? ''}`}
          onClick={visitor.openEntry}
        >
          {viewer.members === 'none' ? 'Sign in to request access' : 'Sign in'}
        </button>
      )}
    </div>
  )
}
